// CustomLink.js
import { Quill } from "react-quill";

// --- Extend the built-in Link blot to remember "target" ---
const BaseLink = Quill.import("formats/link");

class ExtendedLink extends BaseLink {
  static create(value) {
    const node = super.create(typeof value === "object" ? value.href : value);
    if (typeof value === "object") {
      node.setAttribute("target", value.target || "_self");
    } else {
      node.setAttribute("target", "_self");
    }
    node.setAttribute("rel", "noopener noreferrer");
    return node;
  }

  static formats(domNode) {
    return {
      href: domNode.getAttribute("href"),
      target: domNode.getAttribute("target") || "_self",
    };
  }

  // 👇 handle runtime updates via quill.format()
  format(name, value) {
    if (name === "link" && value) {
      if (typeof value === "object") {
        this.domNode.setAttribute("href", value.href);
        this.domNode.setAttribute("target", value.target || "_self");
      } else {
        this.domNode.setAttribute("href", value);
      }
      this.domNode.setAttribute("rel", "noopener noreferrer");
    } else {
      super.format(name, value);
    }
  }
}



Quill.register(ExtendedLink, true);

// --- CustomLink module to add checkbox in tooltip ---
class CustomLink {
  constructor(quill) {
    this.quill = quill;
    this.checkbox = true;

    const toolbar = quill.getModule("toolbar");
    if (toolbar) {
      toolbar.addHandler("link", this.handleLink.bind(this));
    }

    quill.root.addEventListener("click", (e) => {
      const linkEl = e.target.closest("a");
      if (!linkEl) return;

      const blot = Quill.find(linkEl);
      const index = quill.getIndex(blot);
      const length = linkEl.textContent.trim().length;

      quill.setSelection(index, length);

      this.checkbox = (linkEl.getAttribute("target") === "_blank");


      e.preventDefault(); // prevent browser from navigating
      this.handleExistingLink(linkEl);
    });

    // Wait for the tooltip to exist before extending it
    setTimeout(() => this.extendTooltip(), 0);
  }

  handleExistingLink(linkEl) {
    const range = this.quill.getSelection();
    if (!range) return;

    const tooltip = this.quill.theme.tooltip;
    const href = linkEl.getAttribute("href") || "";

    // 🧠 Set up "preview" state like native Quill click does
    tooltip.linkRange = range;
    tooltip.preview.textContent = decodeURI(href);
    tooltip.preview.setAttribute("href", href);

    tooltip.show();
    tooltip.position(this.quill.getBounds(range));
    setTimeout(() => {
    const cb = tooltip.root.querySelector(".ql-newtab-toggle input[type='checkbox']");
    if (cb) cb.checked = this.checkbox;
  }, 0);
  }

  handleLink(value) {
    if (value) {
      const range = this.quill.getSelection();
      if (!range) return;
      this.checkbox = false;
      this.quill.theme.tooltip.edit("link");
      setTimeout(() => {
      const tooltip = this.quill.theme.tooltip;
      const cb = tooltip.root.querySelector(".ql-newtab-toggle input[type='checkbox']");
      if (cb) cb.checked = this.checkbox; // false
    }, 0);
    } else {
      this.quill.format("link", false);
    }
  }

  extendTooltip() {
    const tooltip = this.quill.theme?.tooltip;
    if (!tooltip || !tooltip.root) return;

    // Avoid adding twice
    if (tooltip.root.querySelector(".ql-newtab-toggle")) return;

    // --- Build checkbox UI ---
    const wrapper = document.createElement("label");
    wrapper.classList.add("ql-newtab-toggle");
    Object.assign(wrapper.style, {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      marginTop: "6px",
    });

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.checked = this.checkbox;

    const span = document.createElement("span");
    span.textContent = "Open in new tab";

    wrapper.appendChild(checkbox);
    wrapper.appendChild(span);

    // Insert below link input
    const input = tooltip.root.querySelector('input[data-link]');
    if (input) input.parentNode.insertBefore(wrapper, input.nextSibling);

    // --- Override tooltip.save() ---
    tooltip.save = () => {
      const inputEl = tooltip.root.querySelector('input[data-link]');
      const href = inputEl?.value?.trim();
      const openInNewTab = checkbox.checked;

      if (!href) {
        this.quill.format("link", false);
        tooltip.hide();
        return;
      }

      // ✅ Save through Quill’s model (so it persists)
      this.quill.format("link", {
        href,
        target: openInNewTab ? "_blank" : "_self",
      });


      tooltip.hide();
    };

    // --- Override tooltip.edit() ---
    const originalEdit = tooltip.edit.bind(tooltip);
     tooltip.edit = (mode) => { originalEdit(mode);
      const range = this.quill.getSelection();
      if (!range) return;
      const [leaf] = this.quill.getLeaf(range.index);
      const blot = leaf.parent; 
      if (blot && blot.statics?.blotName === "link") {
        const dom = blot.domNode; 
        const target = dom.getAttribute("target") || "_self"; 
        checkbox.checked = target === "_blank"; 
      } else 
        { checkbox.checked = true; // default 
      }
   };
  }
}

Quill.register("modules/customLink", CustomLink);
